import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import TalentNetwork from "@/components/TalentNetwork";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden pb-20 pt-36 lg:pb-28 lg:pt-44">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.14), rgba(99,102,241,0.06) 45%, transparent 75%)" }} />
      <div className="hero-grid pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-sky-400/25 bg-sky-400/[0.07] px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-sky-300">Recruitment &amp; Talent Acquisition · PAN India</span>
          </motion.div>
          <h1 data-testid="hero-headline" className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Connecting</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-gradient">Exceptional Talent</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>with Exceptional</MaskedLine>
            <MaskedLine delay={0.54}>Opportunities.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400"
            data-testid="hero-subtext"
          >
            Talentify delivers specialised IT and Non-IT recruitment solutions across India, helping businesses identify, attract and hire the right talent faster.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/employers"
              data-testid="hero-hire-talent-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-blue-500 hover:shadow-[0_8px_40px_rgba(59,130,246,0.5)]"
            >
              Hire Talent
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-talk-experts-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-[border-color,background-color] duration-300 hover:border-sky-400/50 hover:bg-sky-400/5"
            >
              <PhoneCall size={16} className="text-sky-400" /> Talk to Our Experts
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
            data-testid="hero-credibility-line"
          >
            9+ Years Recruitment Expertise <span className="mx-2 text-sky-500">|</span> IT &amp; Non-IT Hiring <span className="mx-2 text-sky-500">|</span> PAN India Coverage
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: EASE }}
          className="relative hidden lg:block"
        >
          <TalentNetwork />
        </motion.div>
      </div>
    </section>
  );
}
